import { notify } from 'notiwind';

const MAX_RETRIES = 1;
const RETRY_DELAY = 1000; // 1 second

// How long the page may stay after a 401 sends it to /login before we treat
// the redirect as cancelled (the member chose to stay on an unsaved-changes
// prompt).
export const LOGIN_REDIRECT_GRACE_MS = 5000;

/**
 * Response interceptor for failed axios requests: sends an expired session to
 * /login (rejecting only if the page is still open after the grace period),
 * retries a network error or gateway timeout once, then tells the member and
 * rejects.
 *
 * @param {import('axios').AxiosError} error
 * @param {import('axios').AxiosInstance} axios
 * @returns {Promise<never>}
 */
export async function handleAxiosResponseError(error, axios) {
    const { config, response } = error;

    // Handle 401 unauthorized errors
    if (response?.status === 401) {
        location.href = '/login?loginstatus=autologout';

        // The page is leaving for /login. Don't settle meanwhile, so callers
        // without a .catch can't raise an unhandled rejection (MY10X-207). If
        // the page is still here after the grace period, the redirect was
        // cancelled: reject so the caller can finish its error path. Sentry
        // drops axios 401s (utils/sentryFilters.js).
        return new Promise((resolve, reject) => {
            setTimeout(() => reject(error), LOGIN_REDIRECT_GRACE_MS);
        });
    }

    // Handle network errors (no response from server) and 504 Gateway Timeout
    const isNetworkError = !response && error.message === 'Network Error';
    const isGatewayTimeout = response?.status === 504;

    if (isNetworkError || isGatewayTimeout) {
        // Retry logic for network errors and gateway timeouts
        if (config && config.retryCount < MAX_RETRIES) {
            config.retryCount += 1;
            // Wait before retrying
            await new Promise((resolve) =>
                setTimeout(resolve, RETRY_DELAY * config.retryCount)
            );

            // Retry the request
            return axios(config);
        }

        // Max retries reached, show notification to user
        const errorTitle = isGatewayTimeout ? 'Server Timeout' : 'Network Error';
        const errorText = isGatewayTimeout
            ? 'The server took too long to respond. Please try again.'
            : 'Unable to make request at this time. Please check your network connection.';

        notify(
            {
                group: 'error',
                title: errorTitle,
                text: errorText,
            },
            4000
        );
    }

    return Promise.reject(error);
}
